/**
 * <Row> — horizontal flex container.
 *
 * Replaces ad-hoc `<View style={{ flexDirection: "row", gap, … }}>` in DS-built
 * screens. `gap` is a spacing token (4px scale); `justify` / `align` take the
 * short, prefix-free layout values (see `../layout`).
 */
import React from "react"
import { View, type StyleProp, type ViewStyle } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { type SpacingKey } from "../../../theme/tokens/spacing"
import { alignMap, justifyMap, type Align, type Justify } from "../layout"

export type RowProps = {
    children: React.ReactNode
    /** Space between children — spacing token, defaults to `"2"` (8px). */
    gap?: SpacingKey
    /** Main-axis distribution — defaults to `start`. */
    justify?: Justify
    /** Cross-axis alignment — defaults to `center`. */
    align?: Align
    style?: StyleProp<ViewStyle>
    testID?: string
}

export function Row(props: RowProps) {
    const { children, gap = "2", justify = "start", align = "center", style, testID } = props

    return (
        <View testID={testID} style={[styles.container(gap, justify, align), style]}>
            {children}
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    container: (gap: SpacingKey, justify: Justify, align: Align) => ({
        flexDirection: "row" as const,
        gap: theme.spacing[gap],
        justifyContent: justifyMap[justify],
        alignItems: alignMap[align],
    }),
}))
